// Chakra imports
import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  TabPanels,
  TabPanel,
  Tabs,
  useToast,
  FormLabel, 
  Select
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Custom components
import Card from '../../../../components/card/Card';
import InputField from '../../../../components/fields/InputField';
import TextField from '../../../../components/fields/TextField';
import { addLuggage } from '../hooks';
import { LoadingSpinner } from "../../../../constants/helper";
import { getCarrierOptions } from '../../carriers/hooks';
import { getAirlineOptions } from '../../airlines/hooks';

export default function NewLuggage() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate(); // Initialize the navigate function

  const [values, setValues] = useState({
    referenceNumber: "",
    noOfBags: 0,
  });

  const [carrierOptions, setCarrierOptions] = useState([]);
  const [airlineOptions, setAirlineOptions] = useState([]);

  useEffect(() => {
      const fetchOptions = async () => {
        setLoading(true);
          try {
              const response = await getCarrierOptions();
              setCarrierOptions(response.data);
              
              const airlineResponse = await getAirlineOptions();
              setAirlineOptions(airlineResponse.data);
          } catch (error) {
              console.error("Failed to fetch options:", error);
          } finally{
            setLoading(false);  
          }
      };
      fetchOptions();
  }, []);

  const handleChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  }

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let res = await addLuggage(values);
      if (res.success) {
        toast({
          title: "Luggage added successfully.",
          status: "success", // could be success, error, or info
          duration: 5000, // time in ms before the toast disappears
          isClosable: true, // allows user to manually close the toast
          position: "top-right",
        });
        navigate('/main/luggages/luggages-overview');
      }
      else {
        toast({
          title: "Error",
          description: `${res?.data}`,
          status: "error", // could be success, error, or info
          duration: 5000, // time in ms before the toast disappears
          isClosable: true, // allows user to manually close the toast
          position: "top-right",
        });
      }
    }
    catch (err) {
      toast({
        title: "Error",
        description: `${err}`,
        status: "error", // could be success, error, or info
        duration: 5000, // time in ms before the toast disappears
        isClosable: true, // allows user to manually close the toast
        position: "top-right",
      });
    }
    finally {
      setLoading(false);
    }
  }

  return (
    <>
      {
        loading ? <LoadingSpinner />
          :
          <Flex
            direction="column"
            minH="100vh"
            align="center"
            pt={{ sm: '125px', lg: '75px' }}
            position="relative"
          >
            <Box
              h="45vh"
              bgGradient="linear(to-b, brand.400, brand.600)"
              position="absolute"
              w="100%"
              borderRadius="20px"
            />
            <Tabs
              variant="unstyled"
              zIndex="0"
              mt={{ base: '60px', md: '165px' }}
              display="flex"
              flexDirection="column"
            >
              <TabPanels mt="24px" maxW={{ md: '90%', lg: '100%' }} mx="auto">
                <TabPanel
                  w={{ sm: '330px', md: '700px', lg: '850px' }}
                  p="0px"
                  mx="auto"
                >
                  <Card p="30px">
                    <Text color={textColor} fontSize="2lg" fontWeight="700" mb="20px">
                      Add Customer & Luggage 
                    </Text>
                    <Flex direction="column" w="100%">
                      <Stack direction="column" spacing="20px">
                        <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
                          <InputField
                            mb="0px"
                            id="referenceNumber"
                            placeholder="0000000000"
                            label="Reference Number"
                            name="referenceNumber"
                            value={values?.referenceNumber}
                            autoFocus={true}
                            onChange={handleChange}
                          />
                          <InputField
                            mb="0px"
                            id="noOfBags"
                            placeholder="1"
                            label="No. Of Bags"
                            name="noOfBags"
                            value={values?.noOfBags}
                            type="number"
                            onChange={handleChange}
                          />
                          <InputField
                            mb="0px"
                            id="first"
                            placeholder="eg. Talha"
                            label="Name"
                            name="firstName"
                            value={values?.firstName}
                            onChange={handleChange}
                          />
                          <InputField
                            mb="0px"
                            id="phone-input"
                            type="tel"
                            placeholder="Enter your phone number "
                            label="Phone Number"
                            name="phoneNumber"
                            value={values?.phoneNumber}
                            onChange={handleChange}
                          />
                          <Flex direction="column">
                            <FormLabel
                              ms="10px"
                              htmlFor="currency"
                              fontSize="sm"
                              color={textColor}
                              fontWeight="bold"
                              _hover={{ cursor: 'pointer' }}
                            >
                              Select Carrier
                            </FormLabel>
                            <Select
                              fontSize="sm"
                              id="carrierId"
                              name="carrierId"
                              variant="main"
                              h="44px"
                              maxH="44px"
                              defaultValue="select"
                              value={values?.carrierId}
                              onChange={handleChange}
                            >
                              <option value="select" disabled>Select a Carrier</option>
                              {carrierOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                      {option.label}
                                  </option>
                              ))}
                            </Select>
                          </Flex>
                          <Flex direction="column">
                            <FormLabel
                              ms="10px"
                              htmlFor="currency"
                              fontSize="sm"
                              color={textColor}
                              fontWeight="bold"
                              _hover={{ cursor: 'pointer' }}
                            >
                              Select Airline
                            </FormLabel>
                            <Select
                              fontSize="sm"
                              id="airlineId"
                              name="airlineId"
                              variant="main"
                              h="44px"
                              maxH="44px"
                              defaultValue="select"
                              value={values?.airlineId}
                              onChange={handleChange}
                            >
                              <option value="select" disabled>Select a Airline</option>
                              {airlineOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                      {option.label}
                                  </option>
                              ))}
                            </Select>
                          </Flex>
                          <TextField
                            mb="34px"
                            id="note"
                            type="textarea"
                            placeholder=" "
                            label="Notes"
                            name="notes"
                            value={values?.notes}
                            onChange={handleChange}
                          />
                        </SimpleGrid>
                      </Stack>
                      <Flex justify="space-between" mt="24px">
                        <Button
                          variant="darkBrand"
                          fontSize="sm"
                          borderRadius="16px"
                          w={{ base: '128px', md: '148px' }}
                          h="46px"
                          ms="auto"
                          onClick={handleSubmit}
                        >
                          Submit
                        </Button>
                      </Flex>
                    </Flex>
                  </Card>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Flex>
      }
    </>
  );
}
